import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const STORAGE_KEY = 'fuel72_order_draft';
const STORAGE_VERSION = 1;
const EXPIRATION_DAYS = 7;

export interface OrderFormData {
  selectedPackId: string;
  videoConfigs: any[];
  mainGoal: string;
  googleMapsLink: string;
  referenceUrl: string;
  brandColor: string;
  businessWebsite: string;
  socialMedia: string;
  businessName: string;
  city: string;
  businessType: string;
  targetAudience: string;
  platforms: string[];
  callToAction: string;
  creativeGoal: string;
  vibe: string;
  inspiration: string;
  email: string;
  whatsapp: string;
  deliveryPreference: string;
  selectedAddOns: string[];
  customNotes: string;
}

interface StoredData {
  version: number;
  savedAt: string;
  data: OrderFormData;
}

export function useOrderFormPersistence() {
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasRestoredRef = useRef(false);

  // Load saved data from localStorage
  const loadSavedData = useCallback((): OrderFormData | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const parsed: StoredData = JSON.parse(stored);
      
      // Check version
      if (parsed.version !== STORAGE_VERSION) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      // Check expiration
      const savedDate = new Date(parsed.savedAt);
      const now = new Date();
      const daysDiff = (now.getTime() - savedDate.getTime()) / (1000 * 60 * 60 * 24);
      
      if (daysDiff > EXPIRATION_DAYS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return parsed.data;
    } catch (error) {
      console.error('Failed to load saved order data:', error);
      return null;
    }
  }, []);

  // Save data with debouncing
  const saveData = useCallback((data: OrderFormData) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      try {
        const toStore: StoredData = {
          version: STORAGE_VERSION,
          savedAt: new Date().toISOString(),
          data,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
      } catch (error) {
        console.error('Failed to save order data:', error);
      }
    }, 500);
  }, []);

  // Clear saved data (call after successful checkout)
  const clearSavedData = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
  }, []);

  // Show restoration toast (call once after restoring)
  const showRestorationToast = useCallback(() => {
    if (!hasRestoredRef.current) {
      hasRestoredRef.current = true;
      toast.success('Welcome back! Your progress has been restored.', {
        duration: 4000,
      });
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    loadSavedData,
    saveData,
    clearSavedData,
    showRestorationToast,
  };
}
